import type { CharacterStyle, FontRef, ID, Paragraph, ParagraphStyle, ParaAttrs, Run, TextAttrs } from '@proofroom/schema';

export interface StyleLookup {
  paraStyles: Record<ID, ParagraphStyle>;
  charStyles: Record<ID, CharacterStyle>;
  fonts: Record<ID, FontRef>;
}

/** Sensible defaults when a style id is missing from the snapshot, so rendering never throws. */
export const DEFAULT_PARA_ATTRS: ParaAttrs = {
  fontId: 'HelveticaNeue',
  pointSize: 10,
  leading: 'auto',
  autoLeading: 120,
  tracking: 0,
  kerning: 'metrics',
  fill: { swatch: 'Black', tint: 100, hex: '#000000' },
  ligatures: true,
  capitalization: 'normal',
  baselineShift: 0,
  horizontalScale: 100,
  verticalScale: 100,
  noBreak: false,
  underline: false,
  strikethrough: false,
  language: 'English: UK',
  align: 'left',
  composer: 'paragraph',
  hyphenation: { enabled: false, wordsLongerThan: 5, afterFirst: 2, beforeLast: 2, ladderLimit: 3, zone: 36, lastWord: false, capitalized: true, acrossColumns: true, weight: 5 },
  wordSpacing: { min: 80, desired: 100, max: 133 },
  letterSpacing: { min: 0, desired: 0, max: 0 },
  glyphScaling: { min: 100, desired: 100, max: 100 },
  singleWordJustification: 'left',
  leftIndent: 0,
  rightIndent: 0,
  firstLineIndent: 0,
  spaceBefore: 0,
  spaceAfter: 0,
  alignToBaselineGrid: false,
  keepLinesTogether: false,
  keepFirstLines: 2,
  keepLastLines: 2,
  keepWithNext: 0,
  span: { type: 'none', count: 1, insideGutter: 0, outsideGutter: 0 },
  tabs: [],
};

function stripUndefined<T extends object>(o: T | undefined): Partial<T> {
  const out: Partial<T> = {};
  if (!o) return out;
  for (const [k, v] of Object.entries(o)) if (v !== undefined) (out as Record<string, unknown>)[k] = v;
  return out;
}

export function resolveParaAttrs(para: Pick<Paragraph, 'styleId' | 'overrides'>, lookup: Pick<StyleLookup, 'paraStyles'>): ParaAttrs {
  const style = lookup.paraStyles[para.styleId];
  return { ...DEFAULT_PARA_ATTRS, ...stripUndefined(style?.attrs), ...stripUndefined(para.overrides) };
}

export function resolveRunAttrs(paraAttrs: ParaAttrs, run: Pick<Run, 'charStyleId' | 'overrides'>, lookup: Pick<StyleLookup, 'charStyles'>): TextAttrs {
  const cs = run.charStyleId ? lookup.charStyles[run.charStyleId] : undefined;
  return { ...paraAttrs, ...stripUndefined(cs?.attrs), ...stripUndefined(run.overrides) };
}

/** faceEmphasis is what decides which emphasis marks a run travels with; it lives in the schema so the server's own seed and materialiser cannot disagree with these. */
export { faceEmphasis, resolveFace } from '@proofroom/schema';
